import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { QUOTE_MAX_CHARS } from '@/lib/quoteCarousel/types';
import { wordsToQuoteText, type TranscriptWord } from '@/lib/quoteCarousel/selectTranscriptWords';

interface TranscriptSpanPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Clip-scoped transcript words (already filtered to the kept segments). */
  words: TranscriptWord[];
  onAdd: (quote: { text: string; start: number; end: number }) => void;
}

interface SpanSelection {
  anchor: number;
  focus: number;
  /** True after the first click: the next plain click sets the end. */
  awaitingEnd: boolean;
}

function normalize(value: string): string {
  return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** Dialog to pick a contiguous transcript span as a new slide. Click a word
 *  to set the start, then click (or shift-click) another to set the end. */
export const TranscriptSpanPicker: React.FC<TranscriptSpanPickerProps> = ({
  open, onOpenChange, words, onAdd,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [selection, setSelection] = useState<SpanSelection | null>(null);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setSelection(null);
    }
  }, [open]);

  const visibleWords = useMemo(() => {
    const tokens = normalize(query).split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return words;
    return words.filter((word) => {
      const text = normalize(word.text);
      return tokens.some((token) => text.includes(token));
    });
  }, [query, words]);

  const range = selection
    ? { from: Math.min(selection.anchor, selection.focus), to: Math.max(selection.anchor, selection.focus) }
    : null;
  const previewText = range ? wordsToQuoteText(words, range.from, range.to) : '';
  const tooLong = previewText.length > QUOTE_MAX_CHARS;

  const handleWordClick = (index: number, shiftKey: boolean) => {
    setSelection((current) => {
      if (current && (shiftKey || current.awaitingEnd)) {
        return { anchor: current.anchor, focus: index, awaitingEnd: false };
      }
      return { anchor: index, focus: index, awaitingEnd: true };
    });
  };

  const handleAdd = () => {
    if (!range || !previewText || tooLong) return;
    const first = words.find((word) => word.index === range.from);
    const last = words.find((word) => word.index === range.to);
    if (!first || !last) return;
    onAdd({ text: previewText, start: first.start, end: last.end });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="transcript-span-picker">
        <DialogHeader>
          <DialogTitle>{t('quoteCarousel.picker.title')}</DialogTitle>
          <DialogDescription>{t('quoteCarousel.picker.description')}</DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="transcript-search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('quoteCarousel.picker.searchPlaceholder')}
            className="pl-8"
          />
        </div>

        <div className="max-h-72 overflow-y-auto rounded-md border p-3 text-sm leading-7">
          {words.length === 0 && (
            <p className="text-muted-foreground">{t('quoteCarousel.picker.noTranscript')}</p>
          )}
          {words.length > 0 && visibleWords.length === 0 && (
            <p className="text-muted-foreground">{t('quoteCarousel.picker.noMatches')}</p>
          )}
          {visibleWords.map((word) => {
            const selected = !!range && word.index >= range.from && word.index <= range.to;
            return (
              <button
                key={word.index}
                type="button"
                data-testid={`transcript-word-${word.index}`}
                aria-pressed={selected}
                onClick={(event) => handleWordClick(word.index, event.shiftKey)}
                className={cn(
                  'mr-1 rounded px-0.5 transition-colors hover:bg-accent',
                  selected && 'bg-primary text-primary-foreground hover:bg-primary',
                )}
              >
                {word.text}
              </button>
            );
          })}
        </div>

        <div className="rounded-md bg-muted p-3">
          <p className="mb-1 text-xs font-medium text-muted-foreground">
            {t('quoteCarousel.picker.previewLabel')}
          </p>
          <p className="text-sm" data-testid="transcript-span-preview">
            {previewText || t('quoteCarousel.picker.previewEmpty')}
          </p>
          <p className={cn('mt-1 text-right text-xs', tooLong ? 'text-destructive' : 'text-muted-foreground')}>
            {t('quoteCarousel.slides.charCount', { used: previewText.length, max: QUOTE_MAX_CHARS })}
          </p>
          {tooLong && (
            <p className="text-xs text-destructive" data-testid="transcript-span-too-long">
              {t('quoteCarousel.picker.tooLong', { max: QUOTE_MAX_CHARS })}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t('quoteCarousel.picker.cancel')}
          </Button>
          <Button
            data-testid="transcript-span-add"
            disabled={!previewText || tooLong}
            onClick={handleAdd}
          >
            {t('quoteCarousel.picker.add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TranscriptSpanPicker;
